import {
  FilterOptionList,
  FILTER_POPOVER_CONTENT_CLASS,
  MINIMAL_FILTER_FIELD_CLASS,
  VISIBLE_FILTER_FIELD_CONTROL_CLASS,
  FilterInlineDateCalendar,
  VisibleEnumSelect,
  VisibleTextField,
  FilterPopoverPanel,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@yeshuman/sdk'
import type { FilterDefinition, FilterOption } from '@yeshuman/sdk'
import { CalendarRange, Gauge, CircleDot, ChevronDown } from 'lucide-react'

import type { InsightFilters } from '../hooks/use-insights'

export const INSIGHT_STATUS_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'emerging', label: 'Emerging' },
  { value: 'active', label: 'Active' },
  { value: 'improving', label: 'Improving' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'monitoring', label: 'Monitoring' },
]

function formatStartedAfter(filters: InsightFilters): string {
  return filters.start_date_from
    ? new Date(filters.start_date_from).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'Any time'
}

export function getHealthInsightFilterDefinitions(): FilterDefinition<InsightFilters, undefined>[] {
  return [
    {
      key: 'status',
      label: 'Status',
      icon: CircleDot,
      displayValue: (filters) =>
        INSIGHT_STATUS_OPTIONS.find((o) => o.value === filters.status)?.label ?? 'All',
      isNonDefault: (filters) => Boolean(filters.status),
      reset: (filters) => ({ ...filters, status: undefined }),
      renderField: ({ filters, onChange }) => (
        <VisibleEnumSelect
          value={filters.status ?? 'all'}
          options={INSIGHT_STATUS_OPTIONS}
          onValueChange={(v) => onChange({ ...filters, status: v === 'all' ? undefined : v })}
        />
      ),
      renderPopover: ({ filters, onChange }) => (
        <FilterOptionList
          type="single"
          options={INSIGHT_STATUS_OPTIONS}
          value={filters.status ?? 'all'}
          onValueChange={(v) => onChange({ ...filters, status: v === 'all' ? undefined : v })}
        />
      ),
    },
    {
      key: 'confidence',
      label: 'Min confidence',
      icon: Gauge,
      fieldTooltip: 'Only show insights at or above this confidence (%).',
      displayValue: (filters) =>
        filters.confidence_min !== undefined ? `${Math.round(filters.confidence_min * 100)}%` : '',
      isNonDefault: (filters) => filters.confidence_min !== undefined,
      reset: (filters) => ({ ...filters, confidence_min: undefined }),
      renderField: ({ filters, onChange }) => (
        <VisibleTextField
          value={
            filters.confidence_min !== undefined
              ? String(Math.round(filters.confidence_min * 100))
              : ''
          }
          onValueChange={(value) =>
            onChange({
              ...filters,
              confidence_min: value ? parseFloat(value) / 100 : undefined,
            })
          }
          placeholder="e.g. 70"
        />
      ),
      renderPopover: ({ filters, onChange }) => (
        <div className="space-y-1 p-2">
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="e.g. 70"
            className={MINIMAL_FILTER_FIELD_CLASS}
            value={filters.confidence_min !== undefined ? Math.round(filters.confidence_min * 100) : ''}
            onChange={(e) => {
              const value = e.target.value
              onChange({
                ...filters,
                confidence_min: value ? parseFloat(value) / 100 : undefined,
              })
            }}
          />
        </div>
      ),
    },
    {
      key: 'start_date_from',
      label: 'Started after',
      icon: CalendarRange,
      displayValue: (filters) =>
        filters.start_date_from
          ? new Date(filters.start_date_from).toLocaleDateString(undefined, {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : '',
      isNonDefault: (filters) => Boolean(filters.start_date_from),
      reset: (filters) => ({ ...filters, start_date_from: undefined }),
      renderField: ({ filters, onChange }) => (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              fullWidth
              justify="start"
              className={cn(VISIBLE_FILTER_FIELD_CONTROL_CLASS, 'justify-between font-normal')}
            >
              <span className="truncate">{formatStartedAfter(filters)}</span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className={cn('w-auto min-w-[16rem] p-0', FILTER_POPOVER_CONTENT_CLASS)}
          >
            <FilterPopoverPanel>
              <FilterInlineDateCalendar
                date={filters.start_date_from ? new Date(filters.start_date_from) : undefined}
                onSelect={(d) =>
                  onChange({
                    ...filters,
                    start_date_from: d ? d.toISOString().split('T')[0] : undefined,
                  })
                }
                showClearButton
                initialFocus
              />
            </FilterPopoverPanel>
          </PopoverContent>
        </Popover>
      ),
      renderPopover: ({ filters, onChange }) => (
        <FilterInlineDateCalendar
          date={filters.start_date_from ? new Date(filters.start_date_from) : undefined}
          onSelect={(d) =>
            onChange({ ...filters, start_date_from: d ? d.toISOString().split('T')[0] : undefined })
          }
          showClearButton
          initialFocus
        />
      ),
    },
  ]
}
